import React, { useState, useEffect, useMemo } from 'react';

import { get, mapValues } from 'lodash/fp';
import { Form, Button, Icon } from 'semantic-ui-react';

import { PopupCard, LocalStateDatePicker } from '..';
import { DATE_GROUPINGS, METRIC_COLUMNS, FREQUENCY_OPTIONS, RECURRING_EXPORT_DATE_OPTIONS } from './constants';
import { showToast } from './helpers';
import {
  selectSegmentOptions,
  selectCustomerTypeOptions,
  selectAttributionModelOptions,
  selectFeaturedSegment,
} from './selectors';
import { createDefaultReportName } from './utils';
import { track } from '../../../utils/tracking';
import { postScheduledReportConfiguration } from '../../api/appServices';
import { postExportRequest } from '../../api/datalakeAdhocExports';
import { TIERS } from '../../constants/tiers';
import { useGlobalState } from '../../hooks/global';
import { validateEmail, validateNoSpecialCharacters } from '../../utils/formValidators';
import { PRESET_DATE_RANGES_V3, isLongTermDateRange } from '../../utils/time';

const ScheduledReportsForm = ({
  onFormClose,
  open,
  defaultConversionId,
  defaultCustomerType,
  defaultAttributionModel,
  defaultDateRange,
  segments,
  onPostSuccess,
}) => {
  const { user, account } = useGlobalState();

  // Core state
  const [segment, setSegment] = useState({});
  const [typeOpts, setTypeOpts] = useState([]);
  const [modelOpts, setModelOpts] = useState([]);

  // Form values
  const [convId, setConvId] = useState(defaultConversionId);
  const [type, setType] = useState(defaultCustomerType);
  const [model, setModel] = useState(defaultAttributionModel);
  const [name, setName] = useState('');
  const [dateRange, setDateRange] = useState(defaultDateRange || { startDate: '', endDate: '' });
  const [metrics, setMetrics] = useState([]);
  const [dateGroup, setDateGroup] = useState('none');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [emails, setEmails] = useState([user.email]);
  const [freq, setFreq] = useState('never');
  const [recurDate, setRecurDate] = useState('');

  const isCustomDateRange = dateRange?.numDays === 'custom';
  const hasMultipleEmails = emails.length > 1;

  // Check if current date range is incompatible with scheduling (custom or long-term)
  const isIncompatibleDateRange = isCustomDateRange || isLongTermDateRange(dateRange.numDays);

  const dateRangeOptions = useMemo(() => {
    const allDateRanges = [
      ...PRESET_DATE_RANGES_V3.filter(range => range.numDays !== 'current_month'),
      { text: 'Year to date', numDays: 'year_to_date' },
    ];

    // Disable long-term date ranges when any recurring frequency is selected
    return allDateRanges.map(range => ({
      ...range,
      disabled: freq !== 'never' && isLongTermDateRange(range.numDays),
    }));
  }, [freq]);

  // Initialize or update segment based on convId
  useEffect(() => {
    if (!segments.length) return;
    const newSegment = segments.find(s => s.action_id === defaultConversionId) || selectFeaturedSegment(segments);
    setSegment(newSegment);
    if (!defaultConversionId) {
      setConvId(newSegment.action_id);
    }
  }, [segments, defaultConversionId]);

  // Update available options based on current segment
  useEffect(() => {
    if (!segment.action_id) return;
    setTypeOpts(selectCustomerTypeOptions(segment));
    setModelOpts(selectAttributionModelOptions(segment));
  }, [segment]);

  // Set default values and validate provided values
  useEffect(() => {
    if (!typeOpts.length || !modelOpts.length) return;
    if (!defaultCustomerType) {
      setType(typeOpts[0].value);
    }
    if (!defaultAttributionModel) {
      setModel(modelOpts[0].value);
    } else {
      const modelExists = modelOpts.some(opt => opt.value === defaultAttributionModel);
      setModel(modelExists ? defaultAttributionModel : 'even');
    }
  }, [typeOpts, modelOpts, defaultCustomerType, defaultAttributionModel]);

  // Update report name when fields change
  useEffect(() => {
    if (!segment.action_name || !typeOpts.length || !modelOpts.length) return;

    const typeName = typeOpts.find(opt => opt.value === type)?.text || typeOpts[0].text;
    const modelName = modelOpts.find(opt => opt.value === model)?.text || modelOpts[0].text;

    setName(createDefaultReportName(segment.action_name, typeName, modelName));
  }, [segment, type, model, typeOpts, modelOpts]);

  // Handlers
  const onConversionSelect = (_, { value }) => {
    setConvId(value);
    setSegment(segments.find(s => s.action_id === value));
  };

  const onTypeSelect = (_, { value }) => setType(value);
  const onModelSelect = (_, { value }) => setModel(value);
  const onNameChange = (_, { value }) => setName(value);
  const onMetricsChange = setMetrics;
  const onDateGroupSelect = (_, { value }) => setDateGroup(value);
  const onFreqSelect = (_, { value }) => setFreq(value);
  const onRecurDateSelect = (_, { value }) => setRecurDate(value);

  const onEmailChange = (e, index) => {
    const list = [...emails];
    list[index] = e.target.value;
    setEmails(list);
  };

  const onEmailRemove = index => {
    const list = [...emails];
    list.splice(index, 1);
    setEmails(list);
  };

  const onEmailAdd = () => setEmails([...emails, '']);

  const validateForm = formData => {
    const fields = {
      reportName: {
        key: 'report_name',
        isValid: validateNoSpecialCharacters,
        message: 'Please remove special characters: <> ? [] | * / !',
      },
    };

    const validations = mapValues(field => {
      if (field.isValid(get(field.key)(formData))) return '';
      return field.message;
    })(fields);

    formData?.delivery_options?.email_delivery_location.forEach((value, index) => {
      if (!validateEmail(value.deliveryEmail)) {
        validations[`email${index}`] = 'Please enter a valid email address';
      }
    });

    const isValid = Object.values(validations).every(v => !v);
    if (!isValid) {
      setErrors(validations);
      return false;
    }
    return true;
  };

  const onSubmit = () => {
    const dateRangeValue = typeof dateRange.numDays === 'number' ? dateRange.numDays === 1 ? 'yesterday' : `last_${dateRange.numDays}` : dateRange.numDays;
    const data = {
      pixel_source_name: account.pixel_source_name,
      filter_id: convId,
      start_date: dateRange?.startDate,
      end_date: dateRange?.endDate,
      date_range: dateRangeValue,
      customer_type: type,
      model_type: model,
      group_by_tiers: metrics,
      group_by_period: dateGroup === 'none' ? 'day' : dateGroup,
      report_name: name,
      email_recipients: emails,
      function_name: 'generate-report-export-staging',
    };

    const adhocReportData = {
      report_request: {
        metadata: metrics,
        metadata_filters: [],
        metrics: {
          ntf_filter: type,
          attr_method: model,
          columns: METRIC_COLUMNS.map(metric => metric.value),
        },
        group_by_date: dateGroup,
        advertiser: account.pixel_source_name,
        identifier: convId,
        start_date: dateRange?.startDate,
        end_date: dateRange?.endDate,
      },
      report_options: {
        file_type: 'csv',
      },
      delivery_options: {
        email_delivery_location: emails.map(email => ({ deliveryEmail: email })),
        type: emails.length >= 1 ? 'email' : 'none',
      },
      report_name: name,
      reporter: user.email,
      report_type: 'compiled_mta_tiers',
      report_request_origin: 'ui',
    };

    if (!validateForm(data)) return;

    setIsSubmitting(true);

    if (freq !== 'never') {
      data.cadence = freq;
      data.day_of_week = recurDate;
      data.date_range = dateRangeValue;
      postScheduledReportConfiguration(data)
        .then(() => {
          track('data.exports.scheduled_report.create', {
            pixel_source_name: account.pixel_source_name,
            report_type: 'compiled_mta_tiers',
            conversion: convId,
            cadence: freq,
            date_range: dateRangeValue,
            day_of_week: recurDate === '' ? null : recurDate,
            customer_type: type,
            attribution_model: model,
            tiers_group_by: metrics,
            time_period_group_by: dateGroup,
            start_date: dateRange?.startDate,
            end_date: dateRange?.endDate,
            name,
            email_count: emails.length,
          });
          setIsSubmitting(false);
          showToast({
            type: 'success',
            message: 'Report scheduled successfully!',
            link: {
              href: '/v3/savedreports/all',
              text: 'View in Saved Reports',
            },
          });
          onFormClose(true);
          if (onPostSuccess) {
            onPostSuccess();
          }
        })
        .catch(error => { // eslint-disable-line no-unused-vars
          setIsSubmitting(false);
          showToast({
            type: 'error',
            message: 'Failed to schedule report. Please try again.',
          });
          onFormClose(true);
        });
    } else {
      postExportRequest(adhocReportData)
        .then(() => {
          track('data.exports.ad_hoc_report.create', {
            pixel_source_name: account.pixel_source_name,
            report_type: 'compiled_mta_tiers',
            conversion: convId,
            customer_type: type,
            attribution_model: model,
            tiers_group_by: metrics,
            time_period_group_by: dateGroup,
            start_date: dateRange?.startDate,
            end_date: dateRange?.endDate,
            name,
            email_count: emails.length,
          });
          setIsSubmitting(false);
          showToast({
            type: 'success',
            message: 'Report submitted successfully!',
            link: {
              href: '/v3/savedreports/all',
              text: 'View in Saved Reports',
            },
          });
          onFormClose(true);
          if (onPostSuccess) {
            onPostSuccess();
          }
        })
        .catch(error => { // eslint-disable-line no-unused-vars
          setIsSubmitting(false);
          showToast({
            type: 'error',
            message: 'Failed to submit report. Please try again.',
          });
          onFormClose(true);
        });
    }
  };

  return (
    <>
      <h2>Generate Report</h2>
      <Form
        onSubmit={onSubmit}
        loading={isSubmitting}
      >
        <Form.Field>
          <label>Conversion</label>
          <Form.Select
            value={convId}
            options={selectSegmentOptions(segments)}
            onChange={onConversionSelect}
          />
        </Form.Field>
        <Form.Field>
          <label>Customer Type</label>
          <Form.Select
            value={type}
            options={typeOpts}
            onChange={onTypeSelect}
          />
        </Form.Field>
        <Form.Field>
          <label>Attribution Model</label>
          <Form.Select
            value={model}
            options={modelOpts}
            onChange={onModelSelect}
          />
        </Form.Field>
        <Form.Field>
          <label>Date Range</label>
          <LocalStateDatePicker
            onDateChange={setDateRange}
            firstReportingDate={segment.first_reporting_date}
            presetDates={dateRangeOptions}
            startDate={defaultDateRange?.startDate}
            endDate={defaultDateRange?.endDate}
            disableCustomDates={freq !== 'never'}
          />
        </Form.Field>
        <Form.Field>
          <label>Schedule Report Cadence</label>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: 10 }}>Repeat</span>
              <Form.Select
                disabled={isIncompatibleDateRange}
                value={freq}
                options={FREQUENCY_OPTIONS}
                onChange={onFreqSelect}
              />
              {isIncompatibleDateRange && dateRange.numDays !== 'custom' && (
                <span style={{
                  fontSize: '0.8em',
                  color: '#888',
                  marginLeft: 10,
                  fontStyle: 'italic',
                }}
                >
                  (Select shorter date range to enable scheduling)
                </span>
              )}
            </div>
            {freq === 'week' && (
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <span style={{ marginRight: 10, marginLeft: 10 }}>On</span>
                <Form.Select
                  value={recurDate}
                  options={RECURRING_EXPORT_DATE_OPTIONS}
                  onChange={onRecurDateSelect}
                />
              </div>
            )}
          </div>
        </Form.Field>
        <Form.Field>
          <label>Group By Time Period</label>
          <Form.Select
            placeholder="Week"
            value={dateGroup}
            options={DATE_GROUPINGS}
            onChange={onDateGroupSelect}
          />
        </Form.Field>
        <Form.Field>
          <label>Group By Dimensions</label>
          <PopupCard
            list={TIERS.slice(0, 3)}
            originalList={TIERS}
            setOrder={onMetricsChange}
            applyOnDrop={true}
            open={open}
          />
        </Form.Field>
        <Form.Field>
          <label>Metrics</label>
          {METRIC_COLUMNS.map(metric => metric.text).join(', ')}
        </Form.Field>
        <Form.Field>
          <Form.Input
            required
            error={errors.reportName || null}
            name="reportName"
            label="Export Name"
            maxLength="200"
            value={name}
            onChange={onNameChange}
          />
        </Form.Field>
        <Form.Field>
          <label>Email Recipient</label>
          {emails.map((x, i) => (
            <div style={{ display: 'flex' }}>
              <div style={{ flex: 1 }}>
                <Form.Input
                  required
                  placeholder="Email address to deliver the report"
                  value={x}
                  onChange={e => onEmailChange(e, i)}
                />
              </div>
              <div style={{ height: 50, position: 'relative', top: '50%', transform: 'translateY(-15%)', display: 'flex', alignItems: 'bottom' }}>
                {hasMultipleEmails && (
                  <Button icon inverted onClick={() => onEmailRemove(i)}>
                    <Icon color="violet" size="mid" name="cancel" />
                  </Button>
                )}
              </div>
            </div>
          ))}
          <div style={{ color: 'blue', fontSize: 14, fontWeight: 'bold' }}>
            <Button icon inverted onClick={onEmailAdd} style={{ color: 'blue', marginRight: -5 }}>
              <Icon color="violet" size="mid" name="plus circle" />
              Add Recipient
            </Button>
          </div>
        </Form.Field>
        <Button
          disabled={freq === 'week' && !recurDate}
          type="submit"
          primary
        >
          {freq === 'never' ? 'Send Report' : 'Schedule Report'}
        </Button>
        <Button type="button" onClick={() => onFormClose()}>Cancel</Button>
      </Form>
    </>
  );
};

export default ScheduledReportsForm;
